import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Link } from '@/components/LocalizedLink';
import { useLanguage } from '@/contexts/LanguageContext';
import MagneticButton from '@/components/MagneticButton';
import FloatingAsset from '@/components/FloatingAsset';
import assetCursor from '@/assets/asset-chrome-cursor.webp';
import ProjectCardLink from '@/components/ProjectCardLink';
import { portfolioProjects, type PortfolioProject } from '@/lib/portfolioProjects';

const ProjectCard = ({ project, index, isInView }: { project: PortfolioProject; index: number; isInView: boolean }) => {
  const { t, lang } = useLanguage();
  const title = t(`projects.${project.key}.title`);
  const cardRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [30, -30]);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="group"
    >
      <ProjectCardLink
        project={project}
        label={lang === 'tr' ? `${title} canlı sitesini aç` : `Open ${title} live site`}
        className="block cursor-pointer"
      >
      <div className="relative overflow-hidden rounded-2xl bg-secondary aspect-[3/2] mb-5">
        <motion.img
          src={project.image}
          alt={t(`projects.${project.key}.title`)}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          style={{ y: imgY }}
          loading="lazy"
          width={1200}
          height={800}
        />
        <motion.div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/5 transition-colors duration-500" />
        <motion.div
          className="absolute bottom-5 right-5 w-10 h-10 rounded-full bg-background flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          whileHover={{ scale: 1.1 }}
        >
          <span className="text-foreground text-sm">{project.liveUrl ? '↗' : '→'}</span>
        </motion.div>
      </div>
      </ProjectCardLink>
      <div className="flex items-start justify-between gap-4">
        <div>
          <ProjectCardLink project={project} label={title} className="block">
            <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-foreground/70 transition-colors duration-300">
              {title}
            </h3>
          </ProjectCardLink>
          <p className="font-body text-sm text-muted-foreground mt-1.5 leading-relaxed max-w-sm">
            {t(`projects.${project.key}.desc`)}
          </p>
          {project.liveUrl ? (
            <Link
              to={`/projects/${project.slug}`}
              className="inline-block mt-3 font-body text-xs tracking-wide uppercase text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('projects.view')} →
            </Link>
          ) : null}
        </div>
        <div className="hidden sm:flex flex-wrap gap-1.5 mt-1">
          {t(`projects.${project.key}.tags`).split(',').map((tag) => (
            <span key={tag} className="font-body text-[10px] text-muted-foreground bg-secondary px-2.5 py-1 rounded-full">
              {tag.trim()}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  );
};

const ProjectsSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  const projects = portfolioProjects;

  return (
    <section className="section-y relative overflow-hidden" id="projects">
      <FloatingAsset src={assetCursor} size={120} className="top-10 right-10 opacity-40" floatDuration={8} />

      <div ref={ref} className="container mx-auto px-6 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="section-head flex items-end justify-between"
        >
          <div>
            <p className="font-body text-xs tracking-[0.2em] text-muted-foreground mb-3 uppercase">
              {t('projects.tag')}
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-foreground">
              {t('projects.title')}
            </h2>
          </div>
          <MagneticButton>
            <Link
              to="/projects"
              className="hidden md:inline-block font-body text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t('projects.all')} →
            </Link>
          </MagneticButton>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-10">
          {projects.map((project, i) => (
            <ProjectCard key={project.key} project={project} index={i} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;
